import React, { FormEvent, useEffect, useRef, useState } from 'react';
import {
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ArrowUp, Mic } from 'react-feather';
import useSpeechRecognition from 'src/hooks/useSpeechRecognition';
import {
    ASSISTANT_MESSAGE_MAX_LENGTH,
    ASSISTANT_NAME,
    SPEECH_PERMISSION_ERRORS,
} from 'src/modules/assistant/constants/widget';
import { useComposerStyles } from './styles';

const MIC_ICON_SIZE = 17;
const SEND_ICON_SIZE = 18;

interface AssistantComposerProps {
    disabled: boolean;
    onSubmit: (text: string) => void;
    onListeningChange: (listening: boolean) => void;
}

/**
 * Message input with optional voice dictation. Remount it (via `key`) to clear the input.
 */
export const AssistantComposer = ({
    disabled,
    onSubmit,
    onListeningChange,
}: AssistantComposerProps) => {
    const classes = useComposerStyles();
    const { t, i18n } = useTranslation();
    const [input, setInput] = useState('');
    const {
        error: speechError,
        isListening,
        isSupported,
        stopListening,
        toggleListening,
    } = useSpeechRecognition({
        language: i18n.language,
        onTranscript: (transcript: string) =>
            setInput(transcript.slice(0, ASSISTANT_MESSAGE_MAX_LENGTH)),
    });
    const onListeningChangeRef = useRef(onListeningChange);
    onListeningChangeRef.current = onListeningChange;

    useEffect(() => {
        onListeningChangeRef.current(isListening);
    }, [isListening]);

    useEffect(() => () => onListeningChangeRef.current(false), []);

    useEffect(() => {
        if (disabled) stopListening();
    }, [disabled, stopListening]);

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (!input.trim()) return;
        stopListening();
        setInput('');
        onSubmit(input);
    };

    const voiceLabel = isListening
        ? t('Assistant.StopVoiceInput')
        : t('Assistant.StartVoiceInput');

    return (
        <form className={classes.composer} onSubmit={handleSubmit}>
            <TextField
                fullWidth
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            {isSupported && (
                                <Tooltip title={voiceLabel}>
                                    <span>
                                        <IconButton
                                            aria-label={voiceLabel}
                                            aria-pressed={isListening}
                                            className={
                                                isListening
                                                    ? classes.listening
                                                    : undefined
                                            }
                                            color={
                                                isListening
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                            disabled={disabled}
                                            onClick={() =>
                                                toggleListening(input)
                                            }
                                            size="small"
                                        >
                                            <Mic
                                                aria-hidden="true"
                                                size={MIC_ICON_SIZE}
                                            />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            <IconButton
                                aria-label={t('Assistant.SendMessage')}
                                color="primary"
                                disabled={!input.trim() || disabled}
                                size="small"
                                type="submit"
                            >
                                <ArrowUp
                                    aria-hidden="true"
                                    size={SEND_ICON_SIZE}
                                />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                inputProps={{
                    'aria-label': t('Assistant.MessageInput', {
                        name: ASSISTANT_NAME,
                    }),
                    maxLength: ASSISTANT_MESSAGE_MAX_LENGTH,
                }}
                onChange={(event) => {
                    if (isListening) stopListening();
                    setInput(event.target.value);
                }}
                placeholder={t('Assistant.MessagePlaceholder')}
                size="small"
                value={input}
                variant="outlined"
            />
            {(isListening || speechError) && (
                <Typography
                    align="center"
                    aria-live="polite"
                    color={speechError ? 'error' : 'textSecondary'}
                    component="p"
                    role={speechError ? 'alert' : 'status'}
                    variant="caption"
                >
                    {speechError
                        ? t(
                              SPEECH_PERMISSION_ERRORS.includes(speechError)
                                  ? 'Assistant.VoicePermissionError'
                                  : 'Assistant.VoiceInputError'
                          )
                        : t('Assistant.VoiceListening')}
                </Typography>
            )}
        </form>
    );
};
